/**
 * React Context plumbing for the `CrucibleClient` transport.
 *
 * Consumers reach the active client via `useCrucibleClient()`. The
 * provider is mounted by the bootstrap (`crucible-gui/src/renderer/
 * main.tsx` for the Electron app; `crucible-web/src/main.tsx` for
 * the browser SPA) and passes the appropriate adapter implementation
 * (`ElectronCrucibleClient` or `BrowserCrucibleClient`) down the
 * tree. The client's capability matrix lives on the client itself
 * (instance `capabilities` field) so the provider takes only the
 * `client` prop and capabilities can't mismatch the implementation
 * (codex round-1 L1/M2).
 *
 * The hook throws when no provider is mounted — that's a programming
 * error (lifted code rendered outside the provider). The error
 * message points at the bootstrap as the fix-it location.
 *
 * **Client-swap caveat (codex round-1 M3).** If a future bootstrap
 * swaps the `client` prop (e.g. BG-3's auth refresh + WSS reconnect
 * flow), consumers that captured the client via
 * `const client = useCrucibleClient(); useEffect(() => { ... }, [])`
 * will hold a stale reference. Either include `client` in your effect
 * deps explicitly, or use the hook inside the effect each tick
 * (`useEffect(() => { useCrucibleClient()... }, [...])` — but only
 * via a hook, not a captured const). BG-3 ships a stable-proxy
 * variant of `useCrucibleClient` if/when the swap pattern becomes
 * load-bearing.
 */

import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import type { CrucibleClient, ClientCapabilities, ObservingClient } from '@volley/crucible-client';

/**
 * Context value bundles client + capabilities for `O(1)` consumer
 * access (no double-hook + double-context-read on every gate).
 * `null` means no provider mounted — both hooks raise on this.
 */
interface CrucibleClientContextValue {
  client: CrucibleClient;
  capabilities: ClientCapabilities;
}

const CrucibleClientContext = createContext<CrucibleClientContextValue | null>(null);

// The UI package predates its transport provider and still has broad unit
// coverage that mounts leaf components directly. Tests inject an adapter here
// so those mounts exercise the same client API without adding a production
// fallback to the unscoped window bridge. The setter is deliberately unusable
// outside Vitest.
let testOnlyFallbackClient: CrucibleClient | null = null;

export function _setFallbackCrucibleClientForTests(client: CrucibleClient | null): void {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('The fallback Crucible client is test-only.');
  }
  testOnlyFallbackClient = client;
}

const unsubscribeNoop = (): void => undefined;

export interface CrucibleClientProviderProps {
  client: CrucibleClient;
  children: ReactNode;
}

/**
 * Wraps children with the active transport. The bootstrap passes
 * `ElectronCrucibleClient` or `BrowserCrucibleClient`; the provider
 * reads `client.capabilities` off the client itself and subscribes to
 * capability snapshot changes when the client supports them.
 *
 * Args:
 *   props: Provider props carrying the active client and children.
 *
 * Returns:
 *   A React provider element for transport consumers.
 *
 * Raises:
 *   None.
 */
export function CrucibleClientProvider(props: CrucibleClientProviderProps): React.JSX.Element {
  const { client, children } = props;
  const capabilities = useSyncExternalStore(
    (onStoreChange) => client.subscribeCapabilities?.(onStoreChange) ?? unsubscribeNoop,
    () => client.capabilities,
    () => client.capabilities,
  );
  const value = useMemo<CrucibleClientContextValue>(
    () => ({ client, capabilities }),
    [client, capabilities],
  );
  return <CrucibleClientContext.Provider value={value}>{children}</CrucibleClientContext.Provider>;
}

/**
 * Get the active `CrucibleClient`. Throws if no provider is mounted
 * — that's a bootstrap bug, not a runtime condition.
 */
export function useCrucibleClient(): CrucibleClient {
  const ctx = useContext(CrucibleClientContext);
  if (testOnlyFallbackClient && !ctx) return testOnlyFallbackClient;
  if (!ctx) {
    throw new Error(
      'useCrucibleClient: no CrucibleClientProvider mounted. ' +
        'The Electron bootstrap (crucible-gui/src/renderer/main.tsx) ' +
        'must wrap <App /> with <CrucibleClientProvider client={...}>. ' +
        'The browser bootstrap (crucible-web/src/main.tsx) does the ' +
        'same with BrowserCrucibleClient (BG-3).',
    );
  }
  return ctx.client;
}

/** Non-throwing client lookup for inert/SSR-capable components whose transport
 * actions are unavailable until mounted beneath the application provider. */
export function useOptionalCrucibleClient(): CrucibleClient | null {
  return useContext(CrucibleClientContext)?.client ?? testOnlyFallbackClient;
}

/**
 * Get the active client typed as an {@link ObservingClient} — the browser/TV/mobile
 * surface handle that carries the D2 observe path AND the D1 `auth-rejected`
 * lifecycle (`onAuthRejected`). Use this (not {@link useCrucibleClient}, which is
 * typed as the bare `CrucibleClient`) when a React-mounted browser surface needs to
 * `observe()` or subscribe to auth rejection without a cast (D1 TDD F19).
 *
 * THROWS if the active client is not an `ObservingClient` (the Electron bridge —
 * `ElectronCrucibleClient` — implements only the bare `CrucibleClient`): a usage
 * error, surfaced loudly rather than mis-narrowed.
 */
export function useObservingClient(): ObservingClient {
  const client = useCrucibleClient();
  if (typeof (client as Partial<ObservingClient>).observe !== 'function') {
    throw new Error(
      'useObservingClient: the active client is not an ObservingClient — ' +
        'observe()/onAuthRejected are unavailable. Only browser surfaces ' +
        '(BrowserCrucibleClient) expose the D1/D2 surface; the Electron bridge ' +
        'does not.',
    );
  }
  return client as ObservingClient;
}

/**
 * Get the active environment's capability matrix. Used by gating
 * code that distinguishes Electron from browser surfaces (e.g.
 * Settings hides the auto-updater pane when `hasAutoUpdater: false`).
 */
export function useClientCapabilities(): ClientCapabilities {
  const ctx = useContext(CrucibleClientContext);
  if (testOnlyFallbackClient && !ctx) return testOnlyFallbackClient.capabilities;
  if (!ctx) {
    throw new Error(
      'useClientCapabilities: no CrucibleClientProvider mounted. ' +
        'See useCrucibleClient() error for the fix-it location.',
    );
  }
  return ctx.capabilities;
}

/**
 * Non-throwing variant of {@link useClientCapabilities}: returns the
 * active capability matrix, or `null` when no provider is mounted.
 *
 * Use this in components that are ALSO rendered outside a provider — e.g.
 * `<CrucibleErrorState>`, which is unit-tested via `renderToStaticMarkup`
 * with no provider and may surface during early boot before the transport
 * is wired. A throwing hook would crash those render paths; a `null`
 * return lets the caller fall back to a safe default (treat capabilities
 * as unknown / assume the native path).
 */
export function useOptionalClientCapabilities(): ClientCapabilities | null {
  return (
    useContext(CrucibleClientContext)?.capabilities ?? testOnlyFallbackClient?.capabilities ?? null
  );
}
